import React, { useEffect, useMemo, useState } from "react";
import { shuffle, precisionOf, starsOf, Hud, Flash, FLASH_MS } from "./nqShared";

const MISSIONS = [
  { title: "Faire mes devoirs", steps: ["📘 Prendre le cahier de maths", "📖 Ouvrir à la page 36", "👀 Lire la première consigne", "✏️ Faire seulement l'exercice 1"], wrong: ["Tout finir d'un coup", "Faire mes devoirs", "Regarder la télé d'abord", "Penser à tout ce qu'il reste à faire"] },
  { title: "Ranger ma chambre", steps: ["👕 Mettre les vêtements dans le panier", "📚 Poser les livres sur l'étagère", "🧸 Ranger les jouets dans la boîte", "🧹 Vérifier le sol"], wrong: ["Ranger toute la chambre", "Attendre d'avoir envie", "Tout sortir des placards", "Commencer par la salle de bain"] },
  { title: "Préparer mon cartable", steps: ["📋 Regarder la checklist", "📗 Mettre le cahier de français", "✏️ Mettre la trousse", "🥤 Mettre la gourde"], wrong: ["Préparer toutes mes affaires", "Le faire demain matin", "Chercher mon jeu", "Vider tout le sac par terre"] },
  { title: "Préparer mon expédition", steps: ["🗺️ Prendre la carte", "🎒 Ouvrir le sac", "🔦 Mettre la lampe", "🔒 Fermer le sac"], wrong: ["Tout préparer", "Partir tout de suite", "Réfléchir à tout le voyage", "Choisir un déguisement"] },
  { title: "Me préparer le matin", steps: ["🛏️ Me lever", "👕 M'habiller", "🥣 Prendre le petit-déjeuner", "🪥 Me laver les dents"], wrong: ["Être prêt", "Rester au lit encore un peu", "Allumer la tablette", "Chercher mes chaussures partout"] },
];
const CFG = { 1: { missions: 3, options: 3, timer: 0, depth: 2 }, 2: { missions: 3, options: 4, timer: 0, depth: 3 }, 3: { missions: 3, options: 4, timer: 8, depth: 4 } };

export const brief = (level) => {
  const c = CFG[level.n];
  return {
    title: "Par quoi commencer ?",
    goal: "trouver la toute première petite étape pour démarrer.",
    regarde: "Une grande mission s'affiche (par exemple « Faire mes devoirs ») avec plusieurs propositions.",
    fais: "Touche la plus petite chose que tu peux faire tout de suite. Puis choisis l'étape suivante.",
    attention: `Évite les étapes trop grandes (« tout faire ») ou qui n'ont rien à voir avec la mission.${c.timer ? ` Au niveau Maître, tu as ${c.timer} secondes pour choisir : on passe à l'action !` : ""}`,
    preview: { ok: ["📘 Prendre le cahier"], ko: ["Tout finir d'un coup", "Regarder la télé d'abord"], okLabel: "Une petite première étape", koLabel: "Les pièges" },
  };
};

export default function GameStart({ power, level, onFinish, practice, onPracticeEnd }) {
  const cfg = useMemo(() => (practice ? { missions: 1, options: 3, timer: 0, depth: 2 } : CFG[level.n]), [level, practice]);
  const missions = useMemo(() => shuffle(MISSIONS).slice(0, cfg.missions), [cfg.missions]);
  const [m, setM] = useState(0);
  const [step, setStep] = useState(0);
  const [stats, setStats] = useState({ ok: 0, ko: 0, timeout: 0 });
  const [left, setLeft] = useState(cfg.timer);
  const [fb, setFb] = useState(null);
  const mission = missions[m];
  const options = useMemo(() => {
    if (!mission) return [];
    const correct = mission.steps[step];
    const wrongs = shuffle([...mission.wrong, ...mission.steps.slice(step + 1)]).slice(0, cfg.options - 1);
    return shuffle([correct, ...wrongs]);
  }, [mission, step, cfg.options]);

  const advance = (ok) => {
    setStats((s) => ({ ...s, ok: s.ok + (ok ? 1 : 0), ko: s.ko + (ok ? 0 : 1) }));
    setFb(ok ? "ok" : "ko");
    setTimeout(() => {
      setFb(null);
      if (step + 1 < cfg.depth) setStep(step + 1);
      else if (m + 1 < missions.length) { setM(m + 1); setStep(0); }
      else if (practice) onPracticeEnd();
      else {
        const hits = stats.ok + (ok ? 1 : 0); const total = cfg.missions * cfg.depth;
        const precision = precisionOf(hits, total, 0);
        onFinish({ hits, total, errors: total - hits, precision, stars: starsOf(precision), detail: `${hits}/${total} premières étapes bien choisies · ${stats.timeout} hésitation(s) trop longue(s)` });
      }
      setLeft(cfg.timer);
    }, 900);
  };

  useEffect(() => {
    if (!cfg.timer || fb) return undefined;
    const id = setInterval(() => setLeft((l) => l - 1), 1000);
    return () => clearInterval(id);
  }, [cfg.timer, fb, step, m]);
  useEffect(() => { if (cfg.timer && left <= 0 && !fb) { setStats((s) => ({ ...s, timeout: s.timeout + 1 })); advance(false); } }, [left]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="nq-game-start">
      <Hud items={[["Mission", `${m + 1}/${missions.length}`, power.color], ["Étape", `${step + 1}/${cfg.depth}`, "#5E9BD6"], ["Bons choix", stats.ok, "#7CC6A6"], ["Erreurs", stats.ko, "#E0705C"]]} />
      <div className={`relative rounded-3xl p-5 space-y-4 transition-colors ${fb === "ok" ? "bg-[#E4F5EC]" : fb === "ko" ? "bg-[#FDE4DF]" : "bg-[#E4F5EC]/60"}`}>
        <Flash kind={fb} koLabel="✗ Trop grand ou hors sujet !" />
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[var(--ink-soft)]">Grande mission</div>
          <div className="font-serif-title text-2xl sm:text-3xl text-[var(--ink)]" data-testid="nq-start-mission">🚀 {mission.title}</div>
          <div className="text-base font-semibold text-[var(--ink)] mt-1">{step === 0 ? "« Quelle est la plus petite chose que tu peux faire maintenant ? »" : "« Et ensuite, quelle est l'étape suivante ? »"}</div>
          {cfg.timer > 0 && <div className={`mt-2 inline-block px-3 py-1 rounded-full text-white text-xs font-bold ${left <= 3 ? "bg-[#E0705C] animate-pulse" : "bg-[var(--ink)]"}`} data-testid="nq-start-timer">⏱️ {Math.max(0, left)} s</div>}
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {options.map((o, i) => <button key={o} disabled={!!fb} onClick={() => advance(o === mission.steps[step])} data-testid={`nq-start-opt-${i}`} className={`rounded-2xl border-2 px-4 py-3.5 text-left text-base font-medium transition-all active:scale-95 ${fb && o === mission.steps[step] ? "bg-[#7CC6A6] border-[#7CC6A6] text-white" : "bg-white border-[var(--border)] text-[var(--ink)] hover:border-[#7CC6A6]"}`}>{o}</button>)}
        </div>
        {step > 0 && <div className="text-xs text-[var(--ink-soft)] text-center">✅ Déjà fait : {mission.steps.slice(0, step).join(" → ")}</div>}
      </div>
    </div>
  );
}
